import { ReactElement, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { Input, Button, AuthButtons, Loader, getGclid } from '@sc/shared-ui';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useMutation } from '@apollo/client/react';
import {
  GQL_MUTATION_REGISTER,
  GQL_MUTATION_REQUEST_LOGIN_MAGIC_LINK,
  GQL_MUTATION_VERIFY_LOGIN_MAGIC_LINK,
} from '../utility/queries';
import { useRouter, useSearch } from '@tanstack/react-router';
import { CombinedGraphQLErrors } from '@apollo/client';
import { useAlerts } from '../providers/alerts';
import { useAuthenticated } from '../providers/authenticated';
import { useMagicLinkNonce } from '../hooks/useMagicLinkNonce';
import { useOAuthCallback } from '../hooks/useOAuthCallback';
import { trackSignUpConversion } from '../utility/trackSignUpConversion';
import { track } from '../utility/analytics';
import {
  AuthAnnouncement,
  AuthLayout,
  AuthPrivacyNotice,
  AuthSupportLink,
  AuthTitle,
  useResendCountdown,
} from '../components/AuthLayout';
import { notify } from '../utility/notify';
import env from '../environment';

type StepType = 'email' | 'options' | 'sent';

export const Login = (): ReactElement => {
  const router = useRouter();

  const { useApolloError } = useAlerts();

  const { setIsAuthenticated } = useAuthenticated();

  const onError = useApolloError();

  const { issueNonce, consumeNonce } = useMagicLinkNonce();

  const params = useSearch({ from: '/_unauthenticated/login' });

  const [step, setStep] = useState<StepType>(params.email ? 'email' : 'options');

  const [hasFailed, setHasFailed] = useState(false);

  const [isGoogleOnly, setIsGoogleOnly] = useState(false);

  const { remaining, isCountingDown, restart } = useResendCountdown();

  const [register, { loading: isRegistering }] = useMutation(
    GQL_MUTATION_REGISTER,
    { onError }
  );

  const [requestLoginMagicLink, { loading: isRequesting, reset }] = useMutation(
    GQL_MUTATION_REQUEST_LOGIN_MAGIC_LINK,
    {
      onCompleted: () => {
        track('Login Magic Link Requested');

        restart();

        setStep('sent');
      },
      onError: (err) => {
        if (!CombinedGraphQLErrors.is(err)) {
          onError(err);

          return;
        }

        const code = err.errors.find((e) => e.extensions?.code)?.extensions
          ?.code;

        if (code === 'GOOGLE_SSO_REQUIRED') {
          setIsGoogleOnly(true);

          return;
        }

        if (code === 'ACCOUNT_NOT_FOUND') {
          void register({
            variables: {
              input: { email: form.state.values.email, gclid: getGclid() },
            },
            onCompleted: (data) => {
              trackSignUpConversion(data.register.companyUuid);

              track('Sign Up Email Submitted');

              restart();

              setStep('sent');
            },
          });

          return;
        }

        onError(err);
      },
    }
  );

  const requestLink = async (email: string) => {
    const nonce = await issueNonce();

    void requestLoginMagicLink({
      variables: { input: { email, nonce: nonce ?? null } },
    });
  };

  const form = useForm({
    defaultValues: {
      email: params.email ?? '',
    },
    onSubmit: async (form) => requestLink(form.value.email),
  });


  const { isAuthorising } = useOAuthCallback({
    code: params.code,
    state: params.state,
    onCompleted: (registered) =>
      void router.navigate(
        registered ? { to: '/kb', hash: 'create' } : { to: '/' }
      ),
    onFailed: () => void router.navigate({ to: '/login' }),
  });

  const [verifyLoginMagicLink, { loading: isVerifying }] = useMutation(
    GQL_MUTATION_VERIFY_LOGIN_MAGIC_LINK
  );

  // The token is single-use, so it is only redeemed on an explicit click:
  // email security scanners render the page (and run its JS) before the user
  // ever sees it, and would otherwise consume the link on their behalf.
  const redeemMagicLink = () => {
    if (!params.token) return;

    void verifyLoginMagicLink({
      variables: {
        input: {
          token: params.token,
          nonce: consumeNonce() ?? null,
        },
      },
      onCompleted: () => {
        track('Login Completed', { method: 'magic-link' });

        const authDate = new Date().getTime().toString();

        setIsAuthenticated(authDate);

        router.options.context.isAuthenticated = authDate;

        void router.navigate({ to: '/' });
      },
      onError: () => {
        notify.error('Authentication failed, please try again.');

        setHasFailed(true);
      },
    });
  };

  if (isAuthorising || isVerifying)
    return (
      <AuthLayout>
        <div className="space-y-5 text-center items-center flex flex-col">
          <Loader className="size-8" />
          <h3 className="text-h3 font-bold font-grotesque text-center">
            Signing you in
          </h3>
          <span className="text-body-medium">
            Standby, we are just signing you into your account.
          </span>
        </div>
      </AuthLayout>
    );

  if (hasFailed)
    return (
      <AuthLayout showLockUp footer={<AuthSupportLink />}>
        <AuthAnnouncement
          Icon={ExclamationTriangleIcon}
          title="Invalid or expired link"
        >
          This login link is invalid or has expired. Please request a new one.
        </AuthAnnouncement>

        <Button
          button={{
            text: 'Back to login',
            type: 'basic',
            IconStart: ArrowLeftIcon,
            size: 'large',
            onClick: () => {
              setHasFailed(false);

              void router.navigate({ to: '/login', search: {} });
            },
          }}
        />
      </AuthLayout>
    );

  if (params.token)
    return (
      <AuthLayout showLockUp>
        <AuthTitle subtitle="Click below to finish signing in to your account.">
          Sign in to Stark Chat
        </AuthTitle>

        <Button
          button={{
            text: 'Sign in',
            type: 'primary',
            IconEnd: ArrowRightIcon,
            size: 'large',
            onClick: redeemMagicLink,
          }}
        />
      </AuthLayout>
    );

  if (isGoogleOnly)
    return (
      <AuthLayout showLockUp footer={<AuthPrivacyNotice />}>
        <AuthTitle subtitle="This email is linked to a Google account. Continue below to sign in.">
          You currently sign in with Google
        </AuthTitle>

        <div className="w-full flex flex-col gap-3">
          <AuthButtons
            authUrl={env.authUrl}
            appUrl={env.appUrl}
            cognitoClientId={env.cognitoClientId}
            action="login"
          />
          <Button
            button={{
              text: 'Use a different email',
              type: 'basic',
              size: 'large',
              onClick: () => {
                setIsGoogleOnly(false);

                reset();

                form.reset();
              },
            }}
          />
        </div>
      </AuthLayout>
    );

  if (step === 'sent')
    return (
      <AuthLayout
        showLockUp
        loading={isRequesting}
        onBack={() => {
          reset();

          setStep('email');
        }}
      >
        <AuthAnnouncement Icon={EnvelopeIcon} title="We've sent you an email!">
          To continue, click the link sent to{' '}
          <span className="text-white font-semibold">
            {form.state.values.email}
          </span>
        </AuthAnnouncement>

        <div className="w-full space-y-3">
          <Button
            button={
              isCountingDown
                ? {
                    text: `Resend in ${remaining}s`,
                    size: 'large',
                    disabled: true,
                  }
                : {
                    text: 'Resend email',
                    size: 'large',
                    type: 'basic',
                    loading: isRequesting,
                    disabled: isRequesting,
                    onClick: () => void requestLink(form.state.values.email),
                  }
            }
          />
          <Button
            button={{
              text: 'Use a different email',
              type: 'simple',
              size: 'large',
              onClick: () => {
                reset();

                form.reset();

                setStep('email');
              },
            }}
          />
        </div>
      </AuthLayout>
    );

  if (step === 'email')
    return (
      <AuthLayout
        showLockUp
        loading={isRequesting || isRegistering}
        onBack={() => setStep('options')}
        footer={
          <Button
            button={{ text: 'No account? Register', type: 'basic', size: 'large' }}
            link={{ to: '/register' }}
          />
        }
      >
        <AuthTitle>Log in</AuthTitle>

        <form
          onSubmit={(e) => {
            e.stopPropagation();

            e.preventDefault();

            void form.handleSubmit();
          }}
          className="w-full space-y-5"
        >
          <form.Field
            name="email"
            children={(field) => (
              <Input
                value={field.state.value}
                label="Email"
                type="email"
                required
                className="w-full"
                Icon={EnvelopeIcon}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            )}
          />

          <Button
            button={{
              isSubmit: true,
              text: 'Log in',
              IconEnd: ArrowRightIcon,
              size: 'large',
              disabled: isRequesting || isRegistering,
              type: 'primary',
            }}
          />
        </form>
      </AuthLayout>
    );

  return (
    <AuthLayout
      showLockUp
      footer={
        <>
          <Button
            button={{ text: 'No account? Register', type: 'basic', size: 'large' }}
            link={{ to: '/register' }}
          />
          <AuthPrivacyNotice />
        </>
      }
    >
      <AuthTitle subtitle="Select one of the options below">
        Welcome back!
      </AuthTitle>

      <div className="w-full flex flex-col gap-3">
        <AuthButtons
          authUrl={env.authUrl}
          appUrl={env.appUrl}
          cognitoClientId={env.cognitoClientId}
          action="login"
        />
        <Button
          button={{
            text: 'Continue with email',
            IconEnd: EnvelopeIcon,
            type: 'primary',
            size: 'large',
            onClick: () => setStep('email'),
          }}
        />
      </div>
    </AuthLayout>
  );
};
